// ABOUTME: Collects the favorite families, series and items of the loaded catalog tree, for the favorites strip.
// ABOUTME: Applies the stars toggled in the session over the flags the catalog payload was marked with.

import type { ICategory, IProduct } from '@coolshop/cool-model-selection';

import { resolveCategoryNodeId } from '../../utils/resolveProductFavoriteTarget';
import { categoryFavoriteKey, productFavoriteKey } from './CatalogFavoritesContext';

type WithFavorite = { isFavorite?: boolean };

export type FavoriteEntry =
    | { kind: 'category'; category: ICategory; path: ICategory[] }
    | { kind: 'product'; product: IProduct };

/** Every favorite of the loaded catalog tree, families first, in catalog order. */
export function collectCatalogFavorites(
    categories: ICategory[],
    isFavorite: (key: string, marked: boolean | undefined) => boolean,
): FavoriteEntry[] {
    const found: FavoriteEntry[] = [];
    const products: FavoriteEntry[] = [];
    const seenProducts = new Set<string>();
    const walk = (nodes: ICategory[], ancestors: ICategory[]) =>
        nodes.forEach(category => {
            const path = [...ancestors, category];
            const { isFavorite: marked } = category as ICategory & WithFavorite;
            // Keyed by node id, as the star toggles it. A card whose id cannot be decoded has no
            // star to toggle, so only the payload's flag counts for it.
            const nodeId = resolveCategoryNodeId(category);
            const favorite = nodeId
                ? isFavorite(categoryFavoriteKey(nodeId), marked)
                : marked === true;
            if (favorite) {
                found.push({ kind: 'category', category, path });
            }
            (category.products ?? []).forEach(product => {
                const { isFavorite: productMarked } = product as IProduct & WithFavorite;
                if (
                    !seenProducts.has(product.id) &&
                    isFavorite(productFavoriteKey(product.id), productMarked)
                ) {
                    seenProducts.add(product.id);
                    products.push({ kind: 'product', product });
                }
            });
            walk(category.categories ?? [], path);
        });
    walk(categories, []);
    return [...found, ...products];
}
